import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../styles/Ppulbatu/2Photobooth.css';
import '../../styles/IceCream/IceCream.css';
import { getPhotoAreas } from './photoAreas';

function PhotoSelectionIceCream() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const allPhotos = state?.images || [];
  const selectedFrame = state?.selectedFrame;
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const canvasRef = useRef(null);

  const toggleSelect = (photo) => {
    if (selectedPhotos.includes(photo)) {
      setSelectedPhotos(prev => prev.filter(p => p !== photo));
    } else if (selectedPhotos.length < 4) {
      setSelectedPhotos(prev => [...prev, photo]);
    }
  };

  const goToResult = () => {
    navigate('/icecream/result', { state: { images: selectedPhotos, selectedFrame } });
  };

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;

    const singlePhotoWidth = 900;
    canvas.width = singlePhotoWidth;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, 600);

    function calculateCropDimensions(imgWidth, imgHeight, targetWidth, targetHeight) {
      const imgRatio = imgHeight / imgWidth;
      const targetRatio = targetHeight / targetWidth;
      let sourceX, sourceY, sourceWidth, sourceHeight;
      if (imgRatio > targetRatio) {
        sourceWidth = imgWidth;
        sourceHeight = imgWidth * targetRatio;
        sourceX = 0;
        sourceY = (imgHeight - sourceHeight) / 2;
      } else {
        sourceHeight = imgHeight;
        sourceWidth = imgHeight / targetRatio;
        sourceX = (imgWidth - sourceWidth) / 2;
        sourceY = 0;
      }
      return { sourceX, sourceY, sourceWidth, sourceHeight };
    }

    function drawPlaceholder(x, y, width, height) {
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(x, y, width, height);
      ctx.fillStyle = '#999';
      ctx.font = '32px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('', x + width/2, y + height/2);
    }

    if (isMobile) {
      const frameImg = new window.Image();
      frameImg.src = selectedFrame?.frameImage;

      frameImg.onload = () => {
        const maxWidth = Math.min(450, window.innerWidth - 40);
        const scaleFactor = maxWidth / frameImg.naturalWidth;
        const mobileWidth = Math.round(frameImg.naturalWidth * scaleFactor);
        const mobileHeight = Math.round(frameImg.naturalHeight * scaleFactor);

        canvas.width = mobileWidth;
        canvas.height = mobileHeight;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const photoAreas = getPhotoAreas(mobileWidth, mobileHeight);

        let imagesLoaded = 0;
        const totalImages = selectedPhotos.length;

        for (let i = 0; i < 4; i++) {
          const imgSrc = i < selectedPhotos.length ? selectedPhotos[i] : null;
          const area = photoAreas[i];

          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => {
              const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
                img.naturalWidth, img.naturalHeight, area.width, area.height
              );
              ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, area.x, area.y, area.width, area.height);
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
              }
            };
            img.onerror = () => {
              drawPlaceholder(area.x, area.y, area.width, area.height);
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
              }
            };
          } else {
            drawPlaceholder(area.x, area.y, area.width, area.height);
          }
        }

        if (totalImages === 0) {
          ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
        }
      };

      frameImg.onerror = () => {
        const mobileWidth = Math.min(400, window.innerWidth - 40);
        const mobileHeight = Math.round(mobileWidth * 4 / 3);
        canvas.width = mobileWidth;
        canvas.height = mobileHeight * 4;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < 4; i++) {
          const imgSrc = i < selectedPhotos.length ? selectedPhotos[i] : null;
          const y = i * mobileHeight;
          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => { ctx.drawImage(img, 0, y, mobileWidth, mobileHeight); };
          }
        }
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 3;
        ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);
      };

      return;
    }

    const frameImg = new window.Image();
    frameImg.src = selectedFrame?.frameImage;

    frameImg.onload = () => {
      const frameNaturalWidth = frameImg.naturalWidth;
      const frameNaturalHeight = frameImg.naturalHeight;
      const frameAspectRatio = frameNaturalHeight / frameNaturalWidth;

      const maxFrameWidth = singlePhotoWidth;
      const maxFrameHeight = 1200;

      let frameDisplayWidth, frameDisplayHeight;

      if (frameAspectRatio > maxFrameHeight / maxFrameWidth) {
        frameDisplayHeight = maxFrameHeight;
        frameDisplayWidth = frameDisplayHeight / frameAspectRatio;
      } else {
        frameDisplayWidth = maxFrameWidth;
        frameDisplayHeight = frameDisplayWidth * frameAspectRatio;
      }

      canvas.width = frameDisplayWidth;
      canvas.height = frameDisplayHeight;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const photoAreas = getPhotoAreas(frameDisplayWidth, frameDisplayHeight);

      let imagesLoaded = 0;
      const totalImages = selectedPhotos.length;
      const drawFrameOnTop = () => { ctx.drawImage(frameImg, 0, 0, frameDisplayWidth, frameDisplayHeight); };
      const loadedImages = [];

      for (let i = 0; i < 4; i++) {
        const imgSrc = i < selectedPhotos.length ? selectedPhotos[i] : null;
        const area = photoAreas[i];

        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth, img.naturalHeight, area.width, area.height
            );
            ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, area.x, area.y, area.width, area.height);
            loadedImages.push(i);
            if (loadedImages.length === totalImages) { drawFrameOnTop(); }
          };
          img.onerror = () => {
            drawPlaceholder(area.x, area.y, area.width, area.height);
            loadedImages.push(i);
            if (loadedImages.length === totalImages) { drawFrameOnTop(); }
          };
        } else {
          drawPlaceholder(area.x, area.y, area.width, area.height);
        }
      }

      if (totalImages === 0) { drawFrameOnTop(); }
    };

    frameImg.onerror = () => {
      const singlePhotoHeight = Math.round(singlePhotoWidth * 4 / 3);
      canvas.width = singlePhotoWidth;
      canvas.height = singlePhotoHeight * 4;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < 4; i++) {
        const imgSrc = i < selectedPhotos.length ? selectedPhotos[i] : null;
        const y = i * singlePhotoHeight;
        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth, img.naturalHeight, singlePhotoWidth, singlePhotoHeight
            );
            ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, y, singlePhotoWidth, singlePhotoHeight);
          };
          img.onerror = () => { drawPlaceholder(0, y, singlePhotoWidth, singlePhotoHeight); };
        } else {
          drawPlaceholder(0, y, singlePhotoWidth, singlePhotoHeight);
        }
      }
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 10;
      ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);
    };
  }, [selectedPhotos, selectedFrame]);

  return (
    <div className="photo-selection-container IceCreamBg" style={{ backgroundImage: "url('/img/IceCream/Background.PNG')" }}>
      <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Choose your favorite 4 photos</h2>
      <div className="selection-content">
        <div className="photo-grid-section">
          <div className="photo-gridTMRW">
            {allPhotos.map((photo, idx) => (
              <div key={idx} className="thumbnail-container">
                <img
                  src={photo}
                  alt={`Photo ${idx + 1}`}
                  className={`photo-thumbnail ${selectedPhotos.includes(photo) ? 'selected' : ''}`}
                  onClick={() => toggleSelect(photo)}
                />
                {selectedPhotos.includes(photo) && (
                  <div className="photo-number">
                    {selectedPhotos.indexOf(photo) + 1}
                  </div>
                )}
              </div>
            ))}

            {Array.from({ length: Math.max(0, 8 - allPhotos.length) }).map((_, idx) => (
              <div key={`empty-${idx}`} className="thumbnail-container">
                <div className="empty-slot"></div>
              </div>
            ))}

            <div className="thumbnail-container status-card">
              <div className="status-content">
                <div className="status-number">{selectedPhotos.length}/4</div>
                <div className="status-label">Selected</div>
              </div>
            </div>
          </div>

          <button
            className="next-button"
            onClick={goToResult}
            disabled={selectedPhotos.length !== 4}
          >
            Create Photo Strip
          </button>
        </div>

        <div className="frame-preview-container">
          <div className="frame-preview">
            <canvas
              ref={canvasRef}
              className="photo-strip"
              style={{ maxWidth: '450px', width: '100%', height: 'auto' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhotoSelectionIceCream;
